/**
 * <bg-ribbons> — canvas-based 3D-feeling ribbon background.
 *
 * Spawns curving Bezier ribbons across the viewport. Every property of
 * a ribbon is driven by a unique soundtrack stem, so the visual maps
 * 1:1 onto the music:
 *
 *   Lead vocals    → SPAWN RATE (more vocals = more ribbons born)
 *   Backing vocals → HUE drift (harmonies = colour wash)
 *   Drums          → SPEED (rhythm = how fast each ribbon traces)
 *   Bass           → THICKNESS (sub-bass = weighty ribbons)
 *   Guitar         → CURVATURE (plucky transients = sharper bends)
 *   Percussion     → SPARKLE (tambourine ticks = head sparkle)
 *   Strings        → LIFETIME (sustained pads = long-lived ribbons)
 *   Synth          → GLOW intensity (washes = luminous halos)
 *
 * The element fills the viewport (position:fixed inset:0), composites
 * via mix-blend-mode: screen so colour accumulates without crushing
 * the foreground UI. Pure Canvas2D — no WebGL, no DOM nodes per ribbon.
 */

import { getCanvasDpr, getFrameBudgetMs, getParticleScale, shouldRender } from "./perf-budget.ts";

const MAX_RIBBONS = Math.max(2, Math.floor(24 * getParticleScale()));
const SPAWN_COOLDOWN_MS = 700;   // 350 → 700 (halve spawn rate)
const FRAME_BUDGET_MS = getFrameBudgetMs("atmosphere");

type Ribbon = {
  x0: number;
  y0: number;
  cx1: number;
  cy1: number;
  cx2: number;
  cy2: number;
  x1: number;
  y1: number;
  hueBase: number;       // 0..360
  thickness: number;     // 1..8 base px
  speed: number;         // bezier-progress per second (0.05..0.6)
  progress: number;      // 0..1 head position
  tail: number;          // 0.10..0.35 of arc visible
  lifeMs: number;
  bornAt: number;
  sparklePhase: number;
};

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

// Was reading via getComputedStyle (1 sync style flush per stem per
// frame). Now reads from the in-memory frame bus — pure object access,
// zero DOM cost.
import { getStem as _readLevel } from "./frame-bus.ts";
import type { StemKey } from "./stem-levels.ts";
const readLevel = (_root: HTMLElement, key: string): number =>
  _readLevel(key as StemKey);

class BgRibbons extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #rafId: number | null = null;
  #ribbons: Ribbon[] = [];
  #lastFrame: number = 0;
  #lastSpawn: number = 0;
  #dpr: number = 1;
  #w: number = 0;
  #h: number = 0;
  #resizeObserver: ResizeObserver | null = null;
  #visibilityHandler: (() => void) | null = null;

  connectedCallback(): void {
    if (!shouldRender("ribbons")) return;
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = this.style.position || "fixed";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.style.zIndex = "0";
    this.style.mixBlendMode = "screen";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;
    this.#resize();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);
    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    this.#resizeObserver?.disconnect();
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#ribbons = [];
  }

  #resize(): void {
    if (this.#canvas === null || this.#ctx === null) return;
    const dpr = getCanvasDpr();          // phone-aware cap (1.0 phone / 1.5 desktop)
    const w = this.clientWidth || window.innerWidth;
    const h = this.clientHeight || window.innerHeight;
    this.#dpr = dpr;
    this.#w = w;
    this.#h = h;
    this.#canvas.width = Math.max(1, Math.floor(w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #spawn(now: number, leadVocals: number, drums: number, bass: number, guitar: number, strings: number, backingVocals: number): void {
    if (this.#ribbons.length >= MAX_RIBBONS) {
      // Cull oldest
      this.#ribbons.shift();
    }

    const w = this.#w;
    const h = this.#h;

    // Random edge entry → random edge exit. Always crosses through the
    // viewport (control points pulled toward the centre with a curvature
    // offset driven by guitar). Creates the "3D path through space" feel.
    type Edge = "T" | "R" | "B" | "L";
    const pickEdge = (): Edge => {
      const r = Math.random();
      if (r < 0.25) return "T";
      if (r < 0.5) return "R";
      if (r < 0.75) return "B";
      return "L";
    };
    const pointOnEdge = (e: Edge): { x: number; y: number } => {
      switch (e) {
        case "T": return { x: rand(0, w), y: -20 };
        case "R": return { x: w + 20, y: rand(0, h) };
        case "B": return { x: rand(0, w), y: h + 20 };
        case "L": return { x: -20, y: rand(0, h) };
      }
    };
    const e0 = pickEdge();
    let e1 = pickEdge();
    while (e1 === e0) e1 = pickEdge();
    const p0 = pointOnEdge(e0);
    const p1 = pointOnEdge(e1);
    const cx = w / 2;
    const cy = h / 2;

    // Curvature: guitar plucks → sharper bends. Default mild S-curve.
    const curveStrength = 0.4 + guitar * 1.1;
    const offset = (Math.random() - 0.5) * Math.min(w, h) * 0.5 * curveStrength;
    const cx1 = cx + offset;
    const cy1 = cy + offset * 0.7 - Math.min(w, h) * 0.15 * curveStrength;
    const cx2 = cx - offset * 0.5;
    const cy2 = cy - offset * 0.4 + Math.min(w, h) * 0.15 * curveStrength;

    // Speed: drums-driven baseline + cap so we don't blast through
    const speed = 0.08 + drums * 0.55;
    // Thickness: bass-driven
    const thickness = 1.5 + bass * 6.5;
    // Lifetime: strings-driven (sustained pads → long ribbons)
    const lifeMs = 2800 + strings * 5200;
    // Hue: brand gold base (50) drifts toward backing-vocals' colour cool
    const hueBase = 38 + backingVocals * 200 + Math.random() * 30;
    // Tail: how much of the arc is visible at any time
    const tail = 0.12 + leadVocals * 0.18;

    this.#ribbons.push({
      x0: p0.x, y0: p0.y,
      cx1, cy1, cx2, cy2,
      x1: p1.x, y1: p1.y,
      hueBase,
      thickness,
      speed,
      progress: 0,
      tail,
      lifeMs,
      bornAt: now,
      sparklePhase: Math.random() * Math.PI * 2,
    });
  }

  #start(): void {
    if (this.#rafId !== null) return;
    this.#lastFrame = performance.now();
    // Frame-budget throttle: only step+render when at least FRAME_BUDGET_MS
    // (33ms on phones, 16ms desktop) has passed since the last paint.
    // RAF still fires every display tick so we can sample timing accurately
    // and resume smoothly, but the heavy work happens at the target rate.
    const tick = (now: number): void => {
      const dt = now - this.#lastFrame;
      if (dt >= FRAME_BUDGET_MS) {
        this.#lastFrame = now;
        this.#step(Math.min(50, dt), now);
        this.#render(now);
      }
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #step(dt: number, now: number): void {
    const root = document.documentElement;
    const lead = readLevel(root, "lead-vocals");
    const backing = readLevel(root, "backing-vocals");
    const drums = readLevel(root, "drums");
    const bass = readLevel(root, "bass");
    const guitar = readLevel(root, "guitar");
    const strings = readLevel(root, "strings");

    // SPAWN — lead vocals decide rate. POWERHOUSED: ribbons constantly
    // even at silence; peak rate doubled.
    const baselineGap = 700;        // 1 ribbon every 0.7s when silent
    const peakGap = 90;             // ~11 ribbons/sec at full lead
    const targetGap = baselineGap + (peakGap - baselineGap) * lead;
    const cooldown = Math.max(80, targetGap);
    if (now - this.#lastSpawn >= cooldown) {
      this.#spawn(now, lead, drums, bass, guitar, strings, backing);
      this.#lastSpawn = now;
    }

    // ADVANCE — drums-driven multiplier on speed
    const speedMul = 0.6 + drums * 1.8;
    const dts = dt / 1000;
    for (let i = this.#ribbons.length - 1; i >= 0; i -= 1) {
      const r = this.#ribbons[i];
      if (!r) continue;
      r.progress += r.speed * speedMul * dts;
      const age = now - r.bornAt;
      if (r.progress > 1 + r.tail || age > r.lifeMs) {
        this.#ribbons.splice(i, 1);
      }
    }
  }

  #render(now: number): void {
    if (this.#ctx === null) return;
    const ctx = this.#ctx;
    const root = document.documentElement;
    const synth = readLevel(root, "synth");
    const percussion = readLevel(root, "percussion");

    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    for (const r of this.#ribbons) {
      const age = now - r.bornAt;
      const lifeT = Math.min(1, age / r.lifeMs);
      // Envelope: ramp in over first 12%, hold, ramp out last 30%
      let envelope = 1;
      if (lifeT < 0.12) envelope = lifeT / 0.12;
      else if (lifeT > 0.7) envelope = 1 - (lifeT - 0.7) / 0.3;

      // Draw a segment of the bezier from (progress - tail) to progress
      const startT = Math.max(0, r.progress - r.tail);
      const endT = Math.min(1, r.progress);
      if (endT <= 0 || startT >= 1) continue;

      // Sample the bezier in ~24 segments along the visible window
      const segs = 24;
      const pts: Array<{ x: number; y: number; t: number }> = [];
      for (let i = 0; i <= segs; i += 1) {
        const t = startT + (endT - startT) * (i / segs);
        const omt = 1 - t;
        const x = omt * omt * omt * r.x0
                + 3 * omt * omt * t * r.cx1
                + 3 * omt * t * t * r.cx2
                + t * t * t * r.x1;
        const y = omt * omt * omt * r.y0
                + 3 * omt * omt * t * r.cy1
                + 3 * omt * t * t * r.cy2
                + t * t * t * r.y1;
        pts.push({ x, y, t });
      }

      // Render the ribbon as a stroke with tapered width — thin at the
      // tail, thick at the head, then fading out beyond the head.
      const hue = r.hueBase;
      const sat = 80;
      const light = 65;
      const alpha = envelope * (0.5 + 0.5 * (1 - lifeT * 0.4));

      // Glow halo (synth-driven)
      const glowBlur = 6 + synth * 22;
      ctx.shadowColor = `hsla(${hue}, ${sat}%, ${light}%, ${alpha * 0.85})`;
      ctx.shadowBlur = glowBlur;

      // Multiple stroke passes for the "3D" thickness gradient illusion
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Pass 1 — thick base
      ctx.strokeStyle = `hsla(${hue}, ${sat}%, ${light}%, ${alpha * 0.55})`;
      ctx.lineWidth = r.thickness;
      ctx.beginPath();
      for (let i = 0; i < pts.length; i += 1) {
        const p = pts[i];
        if (!p) continue;
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();

      // Pass 2 — bright inner core
      ctx.strokeStyle = `hsla(${hue}, ${Math.min(100, sat + 15)}%, ${Math.min(95, light + 20)}%, ${alpha * 0.85})`;
      ctx.lineWidth = Math.max(0.5, r.thickness * 0.4);
      ctx.shadowBlur = 0;
      ctx.stroke();

      // Sparkle head — bright dot at the leading edge (percussion-driven)
      const head = pts[pts.length - 1];
      if (head !== undefined && percussion > 0.05) {
        const sparkleAmp = (Math.sin(r.sparklePhase + now * 0.012) + 1) / 2;
        const headR = r.thickness * (0.8 + sparkleAmp * percussion * 2);
        ctx.shadowColor = `hsla(${hue}, 100%, 90%, ${alpha})`;
        ctx.shadowBlur = 18 + percussion * 30;
        ctx.fillStyle = `hsla(${hue}, 90%, 92%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(head.x, head.y, headR, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerBgRibbons = (): void => {
  if (customElements.get("bg-ribbons") !== undefined) return;
  customElements.define("bg-ribbons", BgRibbons);
};
